import Popup from 'devextreme-react/popup';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { MyAppsIcon } from './MyAppsIcon';
import { buildIdleLauncherSections, MY_APPS_SEARCH_RESULT_LIMIT, searchMyApps } from './myAppsSearch';
import type { MyAppsSearchResult } from './myAppsTypes';
import './myApps.css';

export interface MyAppsGlobalExperienceProps {
  /** Everything the signed-in user may launch, built by useMyAppsEntries. */
  entries: readonly MyAppsSearchResult[];
  pinnedToolIds: readonly string[];
  recentToolIds: readonly string[];
  onSelect: (result: MyAppsSearchResult) => void;
  onTogglePin: (toolId: string, pinned: boolean) => void;
  onClearRecent: () => void;
}

export function MyAppsGlobalExperience({
  entries,
  pinnedToolIds,
  recentToolIds,
  onSelect,
  onTogglePin,
  onClearRecent
}: MyAppsGlobalExperienceProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const byId = useMemo(() => new Map(entries.map((entry) => [entry.id, entry])), [entries]);
  const pinned = pinnedToolIds.map((id) => byId.get(id)).filter(isSearchResult);
  const recent = recentToolIds.map((id) => byId.get(id)).filter(isSearchResult);
  const trimmedQuery = query.trim();
  const idle = useMemo(() => buildIdleLauncherSections(pinned, recent), [pinned, recent]);
  const results = useMemo(
    () =>
      trimmedQuery
        ? searchMyApps(entries, query).slice(0, MY_APPS_SEARCH_RESULT_LIMIT)
        : idle.items,
    [entries, idle.items, query, trimmedQuery]
  );

  useEffect(() => {
    function onGlobalKeyDown(event: globalThis.KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        setShortcutsOpen(false);
        setPaletteOpen(true);
        return;
      }
      if (event.key === '?' && !isTypingTarget(event.target)) {
        event.preventDefault();
        setPaletteOpen(false);
        setShortcutsOpen(true);
        return;
      }
      if (event.key === 'Escape') {
        setPaletteOpen(false);
        setShortcutsOpen(false);
      }
    }
    window.addEventListener('keydown', onGlobalKeyDown);
    return () => window.removeEventListener('keydown', onGlobalKeyDown);
  }, []);

  useEffect(() => {
    if (!paletteOpen) return;
    setQuery('');
    setSelectedIndex(0);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [paletteOpen]);

  useEffect(() => {
    if (selectedIndex >= results.length) setSelectedIndex(0);
  }, [results.length, selectedIndex]);

  function select(result: MyAppsSearchResult) {
    setPaletteOpen(false);
    onSelect(result);
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((index) => (index + 1) % Math.max(1, results.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(
        (index) => (index - 1 + Math.max(1, results.length)) % Math.max(1, results.length)
      );
    } else if (event.key === 'Enter' && results[selectedIndex]) {
      event.preventDefault();
      select(results[selectedIndex]);
    }
  }

  return (
    <>
      <Popup
        visible={paletteOpen}
        title="Open an app"
        showTitle
        showCloseButton
        hideOnOutsideClick
        width={520}
        maxWidth="calc(100vw - 2rem)"
        maxHeight="70vh"
        height="auto"
        wrapperAttr={{ class: 'my-apps-palette-popup' }}
        onHiding={() => setPaletteOpen(false)}
      >
        <div className="my-apps-palette">
          <label className="my-apps-palette__search">
            <span className="my-apps-sr-only">Search apps</span>
            <input
              ref={inputRef}
              value={query}
              placeholder="Search apps and descriptions…"
              autoComplete="off"
              onChange={(event) => {
                setQuery(event.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={onInputKeyDown}
            />
            <kbd>Esc</kbd>
          </label>

          {!trimmedQuery && idle.pinned.length > 0 ? (
            <ResultSection
              title="Pinned"
              results={idle.pinned}
              pinnedToolIds={pinnedToolIds}
              selectedIndex={selectedIndex}
              selectedOffset={0}
              onSelect={select}
              onTogglePin={onTogglePin}
            />
          ) : null}
          {!trimmedQuery && idle.recent.length > 0 ? (
            <ResultSection
              title="Recently used"
              results={idle.recent}
              pinnedToolIds={pinnedToolIds}
              selectedIndex={selectedIndex}
              selectedOffset={idle.pinned.length}
              action={
                <button type="button" className="my-apps-palette__text-btn" onClick={onClearRecent}>
                  Clear
                </button>
              }
              onSelect={select}
              onTogglePin={onTogglePin}
            />
          ) : null}

          {trimmedQuery ? (
            <ResultSection
              title="Results"
              results={results}
              pinnedToolIds={pinnedToolIds}
              selectedIndex={selectedIndex}
              selectedOffset={0}
              onSelect={select}
              onTogglePin={onTogglePin}
            />
          ) : null}

          {results.length === 0 ? (
            <p className="my-apps-palette__empty" role="status">
              {trimmedQuery
                ? 'No permitted apps match your search.'
                : 'Type to search apps, or pin shortcuts from Browse All Apps.'}
            </p>
          ) : null}

          <footer className="my-apps-palette__hints">
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> Navigate
            </span>
            <span>
              <kbd>Enter</kbd> Open
            </span>
            <span>
              <kbd>Esc</kbd> Close
            </span>
          </footer>
        </div>
      </Popup>

      <Popup
        visible={shortcutsOpen}
        title="Keyboard shortcuts"
        showTitle
        showCloseButton
        hideOnOutsideClick
        width={460}
        height="auto"
        onHiding={() => setShortcutsOpen(false)}
      >
        <dl className="my-apps-shortcuts">
          <div>
            <dt>
              <kbd>Ctrl</kbd>/<kbd>Cmd</kbd> + <kbd>K</kbd>
            </dt>
            <dd>Search and open an app you are permitted to use.</dd>
          </div>
          <div>
            <dt>
              <kbd>?</kbd>
            </dt>
            <dd>Open this shortcut reference when focus is not in a text field.</dd>
          </div>
          <div>
            <dt>
              <kbd>Esc</kbd>
            </dt>
            <dd>Close the active overlay or dialog.</dd>
          </div>
        </dl>
      </Popup>
    </>
  );
}

function ResultSection({
  title,
  results,
  pinnedToolIds,
  selectedIndex,
  selectedOffset,
  action,
  onSelect,
  onTogglePin
}: {
  title: string;
  results: readonly MyAppsSearchResult[];
  pinnedToolIds: readonly string[];
  selectedIndex?: number;
  selectedOffset?: number;
  action?: ReactNode;
  onSelect: (result: MyAppsSearchResult) => void;
  onTogglePin: (toolId: string, pinned: boolean) => void;
}) {
  if (results.length === 0) return null;
  const offset = selectedOffset ?? 0;
  return (
    <section className="my-apps-palette__section">
      <header>
        <p className="my-apps-palette__heading">{title}</p>
        {action}
      </header>
      <div role="listbox" aria-label={title}>
        {results.map((result, index) => {
          const isPinned = pinnedToolIds.includes(result.id);
          const isSelected = selectedIndex === offset + index;
          return (
            <div
              key={`${title}-${result.id}`}
              className={[
                'my-apps-palette__result',
                isSelected ? 'my-apps-palette__result--selected' : ''
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onSelect(result)}
              >
                <MyAppsIcon icon={result.icon} />
                <span>
                  <strong>{result.title}</strong>
                  <small>{result.description}</small>
                </span>
              </button>
              <button
                type="button"
                className="my-apps-palette__pin"
                aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${result.title}`}
                title={`${isPinned ? 'Unpin' : 'Pin'} ${result.title}`}
                onClick={() => onTogglePin(result.id, !isPinned)}
              >
                <i className={`dx-icon dx-icon-${isPinned ? 'pin' : 'unpin'}`} aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

function isSearchResult(result: MyAppsSearchResult | undefined): result is MyAppsSearchResult {
  return result !== undefined;
}
